import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { BookOpen } from "lucide-react";

export default function GettingStartedTab() {
  return (
    <div className="space-y-6">
      {/* Claude Code Quick Start */}
      <Card className="border-2 border-primary/20 bg-primary/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            Quick Start for Claude Code Users
          </CardTitle>
          <CardDescription>
            Get LocalDocs working with Claude Code in 4 simple steps
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-4">
            {/* Step 1 */}
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
                1
              </div>
              <div className="flex-1">
                <p className="font-semibold mb-2">Authenticate the CLI</p>
                <div className="bg-muted p-3 rounded-md font-mono text-sm">
                  npx @projectassistant/localdocs login
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  This opens your browser to authenticate via Bitbucket SSO. Your token is stored in{" "}
                  <code className="text-xs bg-muted px-1 rounded">
                    ~/.localdocs-cli/config.json
                  </code>{" "}
                  and lasts 30 days.
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
                2
              </div>
              <div className="flex-1">
                <p className="font-semibold mb-2">Install the MCP Server</p>
                <div className="bg-muted p-3 rounded-md font-mono text-sm">
                  claude mcp add localdocs -- npx -y @projectassistant/localdocs@latest
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  This registers the LocalDocs MCP server globally for Claude Code. It will be
                  available in all your projects.
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
                3
              </div>
              <div className="flex-1">
                <p className="font-semibold mb-2">Add Instructions to CLAUDE.md</p>
                <p className="text-sm text-muted-foreground mb-2">
                  In your project's <code className="text-xs bg-muted px-1 rounded">CLAUDE.md</code>
                  , add this line:
                </p>
                <div className="bg-muted p-3 rounded-md font-mono text-xs leading-relaxed">
                  - <span className="font-bold">LocalDocs MCP</span> - When user asks to download
                  documentation: 1) `localdocs_search` to find it, 2) `localdocs_export` to
                  `./docs/localdocs/&#123;tech&#125;-docs/`. Always search first.
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  This tells Claude how to use LocalDocs properly in your project.
                </p>
              </div>
            </div>

            {/* Step 4 */}
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
                4
              </div>
              <div className="flex-1">
                <p className="font-semibold mb-2">Restart Claude Code</p>
                <p className="text-sm text-muted-foreground">
                  Exit Claude Code completely and reopen it. The MCP server will now be loaded and
                  you can ask Claude to download documentation like: "Download React 19
                  documentation"
                </p>
              </div>
            </div>
          </div>

          {/* Usage Example */}
          <div className="mt-6 p-4 bg-muted/50 rounded-lg border">
            <h4 className="font-semibold mb-2">✨ Usage Example</h4>
            <p className="text-sm text-muted-foreground mb-3">After setup, simply ask Claude:</p>
            <div className="space-y-2">
              <div className="bg-background p-3 rounded border">
                <p className="text-sm font-medium">"Download FastAPI documentation"</p>
              </div>
              <p className="text-xs text-muted-foreground px-3">
                Claude will automatically search LocalDocs and export to{" "}
                <code className="bg-muted px-1 rounded">docs/localdocs/fastapi-docs/</code>
              </p>
            </div>
          </div>

          {/* Troubleshooting */}
          <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-md">
            <p className="text-sm font-semibold mb-1">💡 Troubleshooting</p>
            <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
              <li>
                If MCP tools aren't showing up, make sure you fully restarted Claude Code (not just
                new chat)
              </li>
              <li>
                Check authentication:{" "}
                <code className="bg-muted px-1 rounded">
                  npx @projectassistant/localdocs status
                </code>
              </li>
              <li>
                Re-login if token expired:{" "}
                <code className="bg-muted px-1 rounded">npx @projectassistant/localdocs login</code>
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* System Overview */}
      <Card className="border-2 border-primary/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            System Overview
          </CardTitle>
          <CardDescription>
            Understanding LocalDocs Hosted architecture and workflow
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-semibold mb-2">What is LocalDocs Hosted?</h4>
            <p className="text-sm text-muted-foreground">
              LocalDocs Hosted is a centralized documentation management system for our agency. It
              provides a single source of truth for all technical documentation, making it easy to
              discover, organize, and share knowledge across projects and team members.
            </p>
          </div>

          <div>
            <h4 className="font-semibold mb-2">How It Works: Document Lifecycle</h4>
            <div className="space-y-3 text-sm text-muted-foreground">
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-semibold">
                  1
                </div>
                <div>
                  <p className="font-semibold text-foreground">Import</p>
                  <p>
                    Add a documentation URL via web UI or CLI. The system fetches the page and
                    converts HTML to clean markdown.
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-semibold">
                  2
                </div>
                <div>
                  <p className="font-semibold text-foreground">Processing</p>
                  <p>
                    Content is processed and stored in Supabase (PostgreSQL). Metadata is extracted
                    and indexed for search.
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-semibold">
                  3
                </div>
                <div>
                  <p className="font-semibold text-foreground">Organization</p>
                  <p>
                    Documents are tagged and categorized. Full-text search and tag filtering make
                    discovery instant.
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-semibold">
                  4
                </div>
                <div>
                  <p className="font-semibold text-foreground">Export & Use</p>
                  <p>
                    Export filtered documentation sets for specific projects. Integrate with Claude
                    Code, AI tools, or use directly in the web UI.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-semibold mb-2">Technical Architecture</h4>
            <div className="grid gap-3 md:grid-cols-2 text-sm">
              <div className="p-3 bg-muted rounded-md">
                <p className="font-semibold mb-1">Frontend</p>
                <p className="text-muted-foreground">React + TypeScript + Vite + shadcn/ui</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Hosted on Vercel for fast global access
                </p>
              </div>
              <div className="p-3 bg-muted rounded-md">
                <p className="font-semibold mb-1">Backend</p>
                <p className="text-muted-foreground">Supabase (PostgreSQL + Edge Functions)</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Row-level security ensures team data isolation
                </p>
              </div>
              <div className="p-3 bg-muted rounded-md">
                <p className="font-semibold mb-1">Storage</p>
                <p className="text-muted-foreground">PostgreSQL with full-text search</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Markdown content stored as text, searchable
                </p>
              </div>
              <div className="p-3 bg-muted rounded-md">
                <p className="font-semibold mb-1">Processing</p>
                <p className="text-muted-foreground">Serverless Edge Functions (Deno)</p>
                <p className="text-xs text-muted-foreground mt-1">
                  HTML → Markdown conversion, quality checks
                </p>
              </div>
              <div className="p-3 bg-muted rounded-md">
                <p className="font-semibold mb-1">AI Assistant</p>
                <p className="text-muted-foreground">Claude Sonnet 4.5 (Anthropic)</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Intelligent document discovery and suggestions
                </p>
              </div>
              <div className="p-3 bg-muted rounded-md">
                <p className="font-semibold mb-1">Security</p>
                <p className="text-muted-foreground">JWT auth + SHA-256 hashing</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Zero trust architecture, RLS enforcement
                </p>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-semibold mb-2">Data Flow</h4>
            <div className="p-4 bg-muted rounded-md font-mono text-xs space-y-1">
              <div>User adds URL → Edge Function fetches page → HTML cleaned</div>
              <div className="ml-4">→ Converted to Markdown → Stored in PostgreSQL</div>
              <div className="ml-4">→ Metadata indexed → Available for search</div>
              <div className="ml-4">→ Tags added → Filterable by team</div>
              <div className="ml-4">→ Export created → Downloaded for projects</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Start */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            Quick Start Guide
          </CardTitle>
          <CardDescription>Get started with LocalDocs Hosted in minutes</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <h3 className="font-semibold">1. Browse Documentation</h3>
              <p className="text-sm text-muted-foreground">
                View all team documentation on the Documents page. Use search (Cmd+K) or filter by
                tags to find what you need.
              </p>
            </div>
            <div className="space-y-2">
              <h3 className="font-semibold">2. Add Documentation</h3>
              <p className="text-sm text-muted-foreground">
                Click "Add Document" for single URLs, or use "AI Discover" for bulk downloads.
                AI-powered discovery suggests comprehensive documentation sets.
              </p>
            </div>
            <div className="space-y-2">
              <h3 className="font-semibold">3. Create Exports</h3>
              <p className="text-sm text-muted-foreground">
                Export documentation in TOC, Claude, or JSON formats. Filter by tags for
                project-specific exports.
              </p>
            </div>
            <div className="space-y-2">
              <h3 className="font-semibold">4. Share with Team</h3>
              <p className="text-sm text-muted-foreground">
                All documentation is shared team-wide. Share useful finds in team chat for better
                collaboration.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
